/**
 * Power Automate API endpoints and authentication scopes
 */

// API Base URLs
// BAP (Business Application Platform) Admin API
// Note: api.powerplatform.com requires geography routing.
// api.bap.microsoft.com is the correct global endpoint for admin operations.
export const POWER_PLATFORM_API_BASE = "https://api.bap.microsoft.com" as const;
export const FLOW_API_BASE = "https://api.flow.microsoft.com" as const;
export const POWERAPPS_API_BASE = "https://api.powerapps.com" as const;
export const MANAGEMENT_API_BASE = "https://management.azure.com" as const;
// Power Pages management lives on the Power Platform API (global host), NOT on the
// BAP host above. It is a distinct token audience (https://api.powerplatform.com).
export const POWER_PAGES_API_BASE = "https://api.powerplatform.com" as const;
export const DATAVERSE_API_BASE = "https://{org}.crm.dynamics.com" as const; // Template - replace {org}
export const GRAPH_API_BASE = "https://graph.microsoft.com" as const;

// API Versions
// 2024-05-01 is the latest stable version for Microsoft.BusinessAppPlatform.
// The former "2022-03-01-preview" was RETIRED by Microsoft — every BAP admin
// call (environments, capacity, billing, governance, apiPolicies) 400s with
// InvalidApiVersion against it. See issue #16.
export const POWER_PLATFORM_API_VERSION = "2024-05-01" as const;
// DLP policies live on a separate provider whose version is carried in the
// PATH, not the api-version query string.
export const POWER_PLATFORM_GOVERNANCE_V1 = "/providers/PowerPlatform.Governance/v1" as const;
export const POWER_PLATFORM_GOVERNANCE_V2 = "/providers/PowerPlatform.Governance/v2" as const;
export const FLOW_API_VERSION = "2016-11-01" as const;
export const POWERAPPS_API_VERSION = "2020-06-01" as const;
export const POWER_PAGES_API_VERSION = "2024-10-01" as const;

// OAuth Scopes
export const SCOPES = {
  POWER_PLATFORM: ["https://api.bap.microsoft.com/.default"],
  // Use Flow Service directly - we have this configured in app registration
  FLOW: ["https://service.flow.microsoft.com/.default"],
  POWERAPPS: ["https://service.powerapps.com/.default"],
  MANAGEMENT: ["https://management.azure.com/.default"],
} as const;

// Power Platform API Resource
export const POWER_PLATFORM_RESOURCE = "https://api.bap.microsoft.com" as const;

// Default Authority
export const DEFAULT_AUTHORITY = "https://login.microsoftonline.com/common" as const;

// WAM Redirect URI template (clientId inserted at runtime)
export const WAM_REDIRECT_URI_TEMPLATE = "ms-appx-web://microsoft.aad.brokerplugin/{clientId}" as const;

// Config paths
export const CONFIG_DIR_NAME = "powerautomate-mcp" as const;
export const CONFIG_FILE_NAME = "config.json" as const;
export const TOKEN_CACHE_FILE_NAME = "token-cache.bin" as const;
export const ACCOUNT_CACHE_FILE_NAME = "account-cache.json" as const;
export const SCHEMA_CACHE_FILE_NAME = "schema-cache.sqlite" as const;

// Rate limiting
export const DEFAULT_MAX_CONCURRENT_REQUESTS = 5 as const;
export const DEFAULT_REQUEST_DELAY_MS = 200 as const;

// Pagination
export const DEFAULT_PAGE_SIZE = 50 as const;
export const MAX_PAGE_SIZE = 250 as const;

// Resource types
export const RESOURCE_TYPES = {
  ENVIRONMENT: "Microsoft.ProcessSimple/environments",
  FLOW: "Microsoft.ProcessSimple/environments/flows",
  CONNECTION: "Microsoft.ProcessSimple/environments/connections",
  CONNECTOR: "Microsoft.PowerApps/apis",
} as const;

// Flow states
export const FLOW_STATES = {
  STARTED: "Started",
  STOPPED: "Stopped",
  SUSPENDED: "Suspended",
} as const;

// Trigger types
export const TRIGGER_TYPES = {
  REQUEST: "Request",
  RECURRENCE: "Recurrence",
  API_CONNECTION: "ApiConnection",
  API_CONNECTION_WEBHOOK: "ApiConnectionWebhook",
  HTTP_WEBHOOK: "HttpWebhook",
} as const;

// Token resource domain allowlist (shared by all auth providers)
// Stored as bare domains — checked with exact match or dot-prefixed suffix
// to prevent subdomain spoofing (e.g., "evil-dynamics.com" won't match "dynamics.com")
export const ALLOWED_TOKEN_DOMAINS = [
  "dynamics.com",
  "microsoft.com",
  "sharepoint.com",
  "office.com",
  "azure.com",
  "windows.net",
  "powerplatform.com",
  "powerapps.com",
] as const;

/**
 * Check if a hostname is in the allowed token domain list.
 * Uses proper domain boundary check: hostname must equal the domain
 * or be a subdomain (dot-separated prefix).
 */
export function isAllowedTokenDomain(hostname: string): boolean {
  // Strip trailing dots (DNS treats "dynamics.com." and "dynamics.com" as equivalent)
  const lower = hostname.toLowerCase().replace(/\.+$/, "");
  // Reject non-ASCII to prevent Unicode homoglyph attacks (e.g., Cyrillic 'а' vs Latin 'a')
  if (/[^\x20-\x7e]/.test(lower)) {
    return false;
  }
  return ALLOWED_TOKEN_DOMAINS.some(
    (d) => lower === d || lower.endsWith(`.${d}`)
  );
}

/**
 * Is this token resource a per-tenant Dataverse organization host?
 *
 * Dataverse audiences are the org's own host (`contoso.crm4.dynamics.com`), so
 * they are dynamic by construction and can never appear in a static resource
 * list — an auth provider that only accepts exact matches against a fixed set
 * rejects every real tenant. Providers accept them by SHAPE instead, using the
 * same `*.dynamics.com` boundary `validateDataverseUrl()` already enforces on
 * the configured environment URL, so the anti-SSRF intent is preserved.
 *
 * Deliberately narrower than {@link isAllowedTokenDomain}: only a bare origin
 * on a SUBDOMAIN of dynamics.com qualifies. Any other dynamic audience should
 * be added on purpose, not inherited here.
 */
export function isDataverseTokenResource(resource: string): boolean {
  if (typeof resource !== "string" || resource.length === 0 || resource.length > 512) {
    return false;
  }
  let url: URL;
  try {
    url = new URL(resource);
  } catch {
    return false;
  }
  // A port survives origin normalization, so it needs its own rule; the default
  // ":443" does not, and is caught by the canonical comparison below.
  if (url.protocol !== "https:" || url.port !== "") return false;
  // The resource must be WRITTEN as the bare origin. Comparing it against the
  // parsed origin rejects credentials, a path, a query, a fragment and a
  // redundant ":443" in one rule — and rejects an IDN homoglyph of the
  // registrable domain ("dynami\u0441s.com") too, because origin is punycoded
  // while the caller's string is not.
  if (resource.toLowerCase().replace(/\/$/, "") !== url.origin.toLowerCase()) return false;
  // Trailing dots are DNS-equivalent. Longer than the suffix means a real
  // organization label is present: the apex is not an organization.
  const host = url.hostname.toLowerCase().replace(/\.+$/, "");
  return host.endsWith(".dynamics.com") && host.length > ".dynamics.com".length;
}

// Action types
export const ACTION_TYPES = {
  OPEN_API_CONNECTION: "OpenApiConnection",
  OPEN_API_CONNECTION_WEBHOOK: "OpenApiConnectionWebhook",
  COMPOSE: "Compose",
  CONDITION: "If",
  FOREACH: "Foreach",
  UNTIL: "Until",
  SCOPE: "Scope",
  SWITCH: "Switch",
  TERMINATE: "Terminate",
  HTTP: "Http",
  PARSE_JSON: "ParseJson",
  INITIALIZE_VARIABLE: "InitializeVariable",
  SET_VARIABLE: "SetVariable",
  INCREMENT_VARIABLE: "IncrementVariable",
  APPEND_TO_ARRAY: "AppendToArrayVariable",
  APPEND_TO_STRING: "AppendToStringVariable",
} as const;
